<template>
  <div class="reportcard-management">
    <div class="air__utils__heading d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-1 font-weight-bold">Exam Report Cards</h5>
        <span class="text-muted font-size-13">Generate, search, and manage individual student report card PDFs for parent download</span>
      </div>
      <div>
        <a-button type="default" icon="reload" :loading="loading" @click="handleRefresh" class="mr-2">
          Refresh
        </a-button>
        <a-radio-group v-model="activeTab" buttonStyle="solid">
          <a-radio-button value="roster">Student Roster</a-radio-button>
          <a-radio-button value="archive">Export Archive</a-radio-button>
        </a-radio-group>
      </div>
    </div>

    <!-- TAB 1: STUDENT ROSTER (PRIMARY WORKSPACE) -->
    <div v-show="activeTab === 'roster'">
      <!-- Filter Toolbar Card -->
      <div class="card mb-3 shadow-sm border-0">
        <div class="card-body py-3">
          <div class="row align-items-end">
            <!-- Session Select -->
            <div class="col-xl-2 col-md-4 col-sm-6 mb-2">
              <label class="font-weight-600 font-size-12 text-uppercase text-muted mb-1 d-block">Academic Session</label>
              <a-select
                style="width: 100%"
                v-model="selectedSession"
                placeholder="Select Session"
                @change="onSessionChange"
              >
                <a-select-option v-for="s in schoolSessions" :key="s.id" :value="s.id">
                  {{ s.name }}
                  <span v-if="s.is_current" class="badge badge-primary font-size-10 ml-1">Current</span>
                </a-select-option>
              </a-select>
            </div>

            <!-- Class Select -->
            <div class="col-xl-2 col-md-4 col-sm-6 mb-2">
              <label class="font-weight-600 font-size-12 text-uppercase text-muted mb-1 d-block">Class</label>
              <a-select
                style="width: 100%"
                v-model="selectedClass"
                placeholder="All Classes"
                allowClear
                :loading="classSpinning"
                @change="onClassChange"
              >
                <a-select-option v-for="c in schoolClasses" :key="c.id" :value="c.id">
                  {{ c.name }}
                </a-select-option>
              </a-select>
            </div>

            <!-- Section Select -->
            <div class="col-xl-2 col-md-4 col-sm-6 mb-2">
              <label class="font-weight-600 font-size-12 text-uppercase text-muted mb-1 d-block">Section</label>
              <a-select
                style="width: 100%"
                v-model="selectedSection"
                placeholder="All Sections"
                allowClear
                :disabled="!selectedClass"
                :loading="sectionSpinning"
                @change="onSectionChange"
              >
                <a-select-option v-for="sec in sections" :key="sec.id" :value="sec.id">
                  {{ sec.name }}
                </a-select-option>
              </a-select>
            </div>

            <!-- Status Filter -->
            <div class="col-xl-2 col-md-4 col-sm-6 mb-2">
              <label class="font-weight-600 font-size-12 text-uppercase text-muted mb-1 d-block">Status</label>
              <a-select
                style="width: 100%"
                v-model="selectedStatus"
                placeholder="All Statuses"
                @change="onStatusFilterChange"
              >
                <a-select-option value="all">All Statuses</a-select-option>
                <a-select-option value="ready">Ready (Generated)</a-select-option>
                <a-select-option value="outdated">Outdated / Needs Update</a-select-option>
                <a-select-option value="generating">In Queue / Generating</a-select-option>
                <a-select-option value="not_generated">Not Generated</a-select-option>
                <a-select-option value="failed">Failed</a-select-option>
              </a-select>
            </div>

            <!-- Search Input -->
            <div class="col-xl-4 col-md-8 col-sm-12 mb-2">
              <label class="font-weight-600 font-size-12 text-uppercase text-muted mb-1 d-block">Search Student</label>
              <a-input-search
                v-model="searchQuery"
                placeholder="Search by name, roll no, or admission code..."
                allowClear
                @search="onSearchSubmit"
                @input="onSearchInput"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- KPI Stats Summary Row -->
      <div class="row mb-3">
        <div class="col-6 col-md-3 col-xl-2 mb-2">
          <div class="card stat-card border-0 shadow-sm text-center py-2 px-3">
            <span class="text-muted font-size-12 font-weight-600 text-uppercase">Total Students</span>
            <h4 class="mb-0 font-weight-bold text-dark">{{ stats.total || 0 }}</h4>
          </div>
        </div>
        <div class="col-6 col-md-3 col-xl-2 mb-2">
          <div class="card stat-card border-0 shadow-sm text-center py-2 px-3 border-left-success">
            <span class="text-success font-size-12 font-weight-600 text-uppercase">Ready</span>
            <h4 class="mb-0 font-weight-bold text-success">{{ stats.ready || 0 }}</h4>
          </div>
        </div>
        <div class="col-6 col-md-3 col-xl-2 mb-2" v-if="stats.outdated > 0">
          <div class="card stat-card border-0 shadow-sm text-center py-2 px-3 border-left-warning">
            <span class="text-warning font-size-12 font-weight-600 text-uppercase">Outdated</span>
            <h4 class="mb-0 font-weight-bold text-warning">{{ stats.outdated || 0 }}</h4>
          </div>
        </div>
        <div class="col-6 col-md-3 col-xl-2 mb-2">
          <div class="card stat-card border-0 shadow-sm text-center py-2 px-3 border-left-primary">
            <span class="text-primary font-size-12 font-weight-600 text-uppercase">In Queue</span>
            <h4 class="mb-0 font-weight-bold text-primary">
              <a-icon v-if="stats.generating > 0" type="loading" class="mr-1" />
              {{ stats.generating || 0 }}
            </h4>
          </div>
        </div>
        <div class="col-6 col-md-3 col-xl-2 mb-2">
          <div class="card stat-card border-0 shadow-sm text-center py-2 px-3 border-left-secondary">
            <span class="text-muted font-size-12 font-weight-600 text-uppercase">Not Generated</span>
            <h4 class="mb-0 font-weight-bold text-secondary">{{ stats.not_generated || 0 }}</h4>
          </div>
        </div>
        <div class="col-6 col-md-3 col-xl-2 mb-2" v-if="stats.failed > 0">
          <div class="card stat-card border-0 shadow-sm text-center py-2 px-3 border-left-danger">
            <span class="text-danger font-size-12 font-weight-600 text-uppercase">Failed</span>
            <h4 class="mb-0 font-weight-bold text-danger">{{ stats.failed || 0 }}</h4>
          </div>
        </div>
      </div>

      <!-- Action & Table Card -->
      <div class="card border-0 shadow-sm">
        <div class="card-body p-3">
          <!-- Bulk Action Bar -->
          <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom">
            <div class="d-flex align-items-center flex-wrap">
              <a-button
                type="primary"
                icon="thunderbolt"
                :loading="bulkGenerating"
                :disabled="!selectedClass"
                @click="() => handleBulkGenerate(false)"
                class="mr-2 mb-2"
              >
                {{ bulkGenerateButtonLabel }}
              </a-button>

              <a-button
                type="dashed"
                icon="calculator"
                :loading="evaluatingSections"
                :disabled="!selectedClass"
                @click="handleReEvaluateSections"
                class="mr-2 mb-2"
              >
                Re-evaluate All Sections
              </a-button>

              <a-button
                type="default"
                icon="sync"
                :loading="bulkGenerating"
                :disabled="selectedRowKeys.length === 0"
                @click="() => handleBulkGenerate(true)"
                class="mr-2 mb-2"
              >
                Regenerate Selected ({{ selectedRowKeys.length }})
              </a-button>

              <span v-if="pollingActive" class="text-primary font-size-12 mb-2 d-inline-flex align-items-center">
                <a-icon type="sync" spin class="mr-1" /> Live sync active
              </span>
            </div>

            <div class="text-muted font-size-13 mb-2">
              Showing {{ studentData.length }} of {{ pagination.total }} students
            </div>
          </div>

          <!-- Roster Data Table -->
          <a-table
            :rowKey="record => record.id"
            :columns="studentColumns"
            :dataSource="studentData"
            :pagination="false"
            :loading="loading"
            :rowSelection="rowSelectionConfig"
            :scroll="{ x: 800 }"
            class="air__utils__scrollTable"
          >
            <!-- Student Code -->
            <template slot="student_code" slot-scope="text">
              <span class="font-weight-600 text-dark">{{ text }}</span>
            </template>

            <!-- Roll No -->
            <template slot="roll_no" slot-scope="text">
              <span class="badge badge-light font-weight-600 px-2 py-1">{{ text || '-' }}</span>
            </template>

            <!-- Student Name -->
            <template slot="student_name" slot-scope="text, record">
              <div class="d-flex align-items-center">
                <div class="avatar-placeholder mr-2 font-weight-bold">
                  {{ (record.student.first_name ? record.student.first_name[0] : '') + (record.student.last_name ? record.student.last_name[0] : '') }}
                </div>
                <div>
                  <div class="font-weight-600 text-dark">
                    {{ record.student.first_name }} {{ record.student.last_name }}
                  </div>
                  <div class="font-size-11 text-muted">ID: {{ record.student.id }}</div>
                </div>
              </div>
            </template>

            <!-- Class & Section -->
            <template slot="class_section" slot-scope="text, record">
              <span>{{ (record.school_class ? record.school_class.name : '') + ' - ' + (record.section ? record.section.name : '') }}</span>
            </template>

            <!-- Status Badge -->
            <template slot="status" slot-scope="text, record">
              <!-- Outdated (Marks, Ranks, or Remarks modified since last export) -->
              <span v-if="record.is_outdated">
                <a-tooltip :title="record.outdated_reason ? (record.outdated_reason + ' since last PDF generation. Click to regenerate.') : 'Marks or remarks modified. Click to regenerate.'">
                  <a-tag color="orange" class="status-tag cursor-pointer" @click="handleSingleGenerate(record)">
                    <a-icon type="warning" class="mr-1" /> Outdated (Needs Update)
                  </a-tag>
                </a-tooltip>
              </span>
              <div v-else-if="record.individual_marksheet">
                <a-tag v-if="record.individual_marksheet.status === 'Exported'" color="green" class="status-tag">
                  <a-icon type="check-circle" class="mr-1" /> Ready
                </a-tag>
                <span v-else-if="record.individual_marksheet.status === 'Initiated'">
                  <a-tag v-if="isRecentlyQueued(record)" color="blue" class="status-tag">
                    <a-icon type="loading" class="mr-1" /> In Queue
                  </a-tag>
                  <a-tooltip v-else title="Marks were edited or generation was stalled. Click to regenerate.">
                    <a-tag color="orange" class="status-tag cursor-pointer" @click="handleSingleGenerate(record)">
                      <a-icon type="warning" class="mr-1" /> Needs Update
                    </a-tag>
                  </a-tooltip>
                </span>
                <a-tooltip v-else-if="record.individual_marksheet.status === 'Failed'" :title="record.individual_marksheet.failed_reason || 'Unknown error during generation'">
                  <a-tag color="red" class="status-tag cursor-pointer">
                    <a-icon type="close-circle" class="mr-1" /> Failed
                  </a-tag>
                </a-tooltip>
                <a-tag v-else color="default" class="status-tag">
                  {{ record.individual_marksheet.status }}
                </a-tag>
              </div>
              <div v-else>
                <a-tag color="default" class="status-tag text-muted">
                  Not Generated
                </a-tag>
              </div>
            </template>

            <!-- Generated At -->
            <template slot="generated_at" slot-scope="text, record">
              <span v-if="record.individual_marksheet && record.individual_marksheet.exported_at" class="font-size-12 text-muted">
                {{ moment(record.individual_marksheet.exported_at).format('DD-MM-YYYY h:mm A') }}
              </span>
              <span v-else class="text-muted font-size-12">-</span>
            </template>

            <!-- File Size -->
            <template slot="file_size" slot-scope="text, record">
              <span v-if="record.individual_marksheet && record.individual_marksheet.size" class="font-size-12">
                {{ record.individual_marksheet.size }} KB
              </span>
              <span v-else class="text-muted font-size-12">-</span>
            </template>

            <!-- Action Buttons -->
            <template slot="actions" slot-scope="text, record">
              <div class="d-flex align-items-center justify-content-end">
                <!-- Preview (only if ready) -->
                <a-tooltip title="Preview PDF in modal">
                  <a-button
                    type="default"
                    size="small"
                    icon="eye"
                    class="mr-1"
                    :disabled="!record.individual_marksheet || record.individual_marksheet.status !== 'Exported'"
                    @click="handlePreview(record)"
                  />
                </a-tooltip>

                <!-- Download (only if ready) -->
                <a-tooltip title="Download PDF">
                  <a-button
                    type="default"
                    size="small"
                    icon="download"
                    class="mr-1"
                    :loading="record.isDownloading"
                    :disabled="!record.individual_marksheet || record.individual_marksheet.status !== 'Exported'"
                    @click="handleDownload(record)"
                  />
                </a-tooltip>

                <!-- Single Regenerate / Generate -->
                <a-tooltip :title="record.individual_marksheet ? 'Regenerate this marksheet' : 'Generate marksheet'">
                  <a-button
                    type="primary"
                    size="small"
                    :icon="record.individual_marksheet ? 'sync' : 'play-circle'"
                    :loading="record.isGenerating"
                    class="mr-1"
                    @click="handleSingleGenerate(record)"
                  />
                </a-tooltip>

                <!-- Delete (if marksheet exists) -->
                <a-popconfirm
                  v-if="record.individual_marksheet"
                  title="Delete this marksheet PDF?"
                  okText="Yes, Delete"
                  cancelText="Cancel"
                  @confirm="handleDelete(record)"
                >
                  <a-button type="danger" size="small" icon="delete" ghost />
                </a-popconfirm>
              </div>
            </template>
          </a-table>

          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
            <span class="text-muted font-size-12">Page {{ pagination.page }} of {{ pagination.totalPages }}</span>
            <a-pagination
              showSizeChanger
              v-model="pagination.page"
              :pageSize.sync="pagination.limit"
              :total="pagination.total"
              :pageSizeOptions="['10', '20', '50', '100']"
              @change="onPageChange"
              @showSizeChange="onPageSizeChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 2: EXPORT ARCHIVE (LEGACY BULK FILES VIEW) -->
    <div v-show="activeTab === 'archive'">
      <div class="card border-0 shadow-sm">
        <div class="card-body">
          <exam-marksheet-export-file-table
            :data="archiveData"
            :loading="archiveLoading"
            @handleUpdateExportFileTable="fetchArchiveFiles"
          />
          <div class="text-right mt-3">
            <a-pagination
              showSizeChanger
              v-model="archivePage"
              :pageSize.sync="archivePageSize"
              :total="archiveMeta.total"
              @change="fetchArchiveFiles"
              @showSizeChange="fetchArchiveFiles"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- PDF PREVIEW MODAL -->
    <a-modal
      :visible="previewModalVisible"
      :title="previewTitle"
      :footer="null"
      width="900px"
      @cancel="previewModalVisible = false"
      :bodyStyle="{ padding: '0px', height: '80vh' }"
      centered
      destroyOnClose
    >
      <div v-if="previewLoading" class="d-flex flex-column align-items-center justify-content-center h-100">
        <a-spin size="large" />
        <p class="mt-3 text-muted font-weight-500">Preparing preview document...</p>
      </div>
      <div v-else-if="previewUrl" class="h-100 d-flex flex-column">
        <iframe
          :src="previewUrl"
          style="width: 100%; height: 100%; border: none;"
          title="Marksheet Preview"
        />
      </div>
      <div v-else class="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
        <a-icon type="frown" class="font-size-36 mb-2" />
        <p>Unable to load PDF preview.</p>
      </div>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { find, debounce } from 'lodash'
import ExamMarksheetExportFileTable from '@/components/exam/reportcard/Table/index'

const studentColumns = [
  {
    title: 'Code',
    dataIndex: 'student_code',
    key: 'student_code',
    width: 120,
    scopedSlots: { customRender: 'student_code' },
    sorter: (a, b) => (a.student_code || '').localeCompare(b.student_code || ''),
  },
  {
    title: 'Roll',
    dataIndex: 'roll_no',
    key: 'roll_no',
    width: 80,
    scopedSlots: { customRender: 'roll_no' },
    sorter: (a, b) => (parseInt(a.roll_no, 10) || 0) - (parseInt(b.roll_no, 10) || 0),
  },
  {
    title: 'Student Name',
    key: 'student_name',
    scopedSlots: { customRender: 'student_name' },
  },
  {
    title: 'Class / Sec',
    key: 'class_section',
    width: 130,
    scopedSlots: { customRender: 'class_section' },
  },
  {
    title: 'Status',
    key: 'status',
    width: 140,
    scopedSlots: { customRender: 'status' },
  },
  {
    title: 'Generated At',
    key: 'generated_at',
    width: 160,
    scopedSlots: { customRender: 'generated_at' },
  },
  {
    title: 'Size',
    key: 'file_size',
    width: 90,
    scopedSlots: { customRender: 'file_size' },
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 150,
    align: 'right',
    scopedSlots: { customRender: 'actions' },
  },
]

export default {
  name: 'ExamReportCardManagement',
  components: {
    ExamMarksheetExportFileTable,
  },
  data() {
    return {
      studentColumns,
      activeTab: 'roster',
      loading: false,
      bulkGenerating: false,
      evaluatingSections: false,
      classSpinning: false,
      sectionSpinning: false,

      // Filter state
      schoolSessions: [],
      schoolClasses: [],
      sections: [],
      selectedSession: undefined,
      selectedClass: undefined,
      selectedSection: undefined,
      selectedStatus: 'all',
      searchQuery: '',

      // Student roster table data
      studentData: [],
      selectedRowKeys: [],
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
      },
      stats: {
        total: 0,
        ready: 0,
        outdated: 0,
        generating: 0,
        not_generated: 0,
        failed: 0,
      },

      // Polling
      pollingTimer: null,
      pollingActive: false,

      // Preview modal
      previewModalVisible: false,
      previewTitle: '',
      previewLoading: false,
      previewUrl: null,

      // Archive tab data
      archiveData: [],
      archiveLoading: false,
      archivePage: 1,
      archivePageSize: 10,
      archiveMeta: { total: 0 },
    }
  },
  computed: {
    rowSelectionConfig() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: this.onSelectChange,
      }
    },
    selectedClassName() {
      if (!this.selectedClass) return ''
      const curClass = find(this.schoolClasses, ['id', this.selectedClass])
      return curClass ? curClass.name : ''
    },
    bulkGenerateButtonLabel() {
      if (!this.selectedClass) return 'Generate All'
      const className = this.selectedClassName
      if (this.selectedSection) {
        const curSec = find(this.sections, ['id', this.selectedSection])
        const secName = curSec ? curSec.name : ''
        return `Generate Section (${secName || className})`
      }
      return `Generate Entire Class (${className})`
    },
  },
  mounted() {
    this.initSessions()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    moment,
    onSelectChange(keys) {
      this.selectedRowKeys = keys
    },

    initSessions() {
      this.$http.get('/api/session')
        .then(({ data }) => {
          this.schoolSessions = data.data
          const current = find(data.data, ['is_current', 1])
          this.selectedSession = current ? current.id : (data.data[0] ? data.data[0].id : undefined)
          this.fetchClasses()
          this.fetchRoster()
          this.fetchArchiveFiles()
        })
        .catch(err => {
          console.error(err)
        })
    },

    fetchClasses() {
      this.classSpinning = true
      this.$http.get('/api/class', {
        params: { session_id: this.selectedSession },
      })
        .then(({ data }) => {
          this.schoolClasses = data.data
          this.classSpinning = false
        })
        .catch(() => {
          this.classSpinning = false
        })
    },

    fetchSections(classId) {
      if (!classId) {
        this.sections = []
        return
      }
      this.sectionSpinning = true
      this.$http.get(`/api/section/class/${classId}`, {
        params: { session_id: this.selectedSession },
      })
        .then(({ data }) => {
          this.sections = data.data
          this.sectionSpinning = false
        })
        .catch(() => {
          this.sectionSpinning = false
        })
    },

    fetchRoster(silent = false) {
      if (!silent) this.loading = true

      const params = {
        session_id: this.selectedSession,
        class_id: this.selectedClass,
        section_id: this.selectedSection,
        status: this.selectedStatus,
        search: this.searchQuery,
        page: this.pagination.page,
        limit: this.pagination.limit,
      }

      this.$http.get('/api/exam/marksheet/individual/list', { params })
        .then(({ data }) => {
          this.loading = false
          this.studentData = (data.data || []).map(item => ({
            ...item,
            isGenerating: false,
          }))

          if (data.meta) {
            this.pagination.total = data.meta.total
            this.pagination.totalPages = data.meta.last_page
          }

          if (data.stats) {
            this.stats = data.stats
          }

          // Check if any item is currently generating to maintain polling
          const hasActiveJobs = this.studentData.some(
            s => this.isRecentlyQueued(s)
          )
          if (hasActiveJobs) {
            this.startPolling()
          } else {
            this.stopPolling()
          }
        })
        .catch(err => {
          this.loading = false
          console.error(err)
        })
    },

    fetchArchiveFiles() {
      this.archiveLoading = true
      this.$http.get('/api/export/all', {
        params: {
          limit: this.archivePageSize,
          page: this.archivePage,
          category: 'Marksheet',
        },
      }).then(({ data }) => {
        this.archiveLoading = false
        this.archiveData = data.data
        this.archiveMeta = data.meta || { total: 0 }
      }).catch(() => {
        this.archiveLoading = false
      })
    },

    onSessionChange(val) {
      this.selectedSession = val
      this.selectedClass = undefined
      this.selectedSection = undefined
      this.pagination.page = 1
      this.fetchClasses()
      this.fetchRoster()
    },

    onClassChange(val) {
      this.selectedClass = val
      this.selectedSection = undefined
      this.pagination.page = 1
      this.fetchSections(val)
      this.fetchRoster()
    },

    onSectionChange(val) {
      this.selectedSection = val
      this.pagination.page = 1
      this.fetchRoster()
    },

    onStatusFilterChange() {
      this.pagination.page = 1
      this.fetchRoster()
    },

    onSearchSubmit() {
      this.pagination.page = 1
      this.fetchRoster()
    },

    onSearchInput: debounce(function() {
      this.pagination.page = 1
      this.fetchRoster()
    }, 400),

    onPageChange(page) {
      this.pagination.page = page
      this.fetchRoster()
    },

    onPageSizeChange(current, size) {
      this.pagination.limit = size
      this.pagination.page = 1
      this.fetchRoster()
    },

    handleRefresh() {
      if (this.activeTab === 'roster') {
        this.fetchRoster()
      } else {
        this.fetchArchiveFiles()
      }
    },

    isRecentlyQueued(record) {
      if (!record || !record.individual_marksheet || record.individual_marksheet.status !== 'Initiated') {
        return false
      }
      const updated = record.individual_marksheet.updated_at || record.individual_marksheet.created_at
      if (!updated) return false
      return moment().diff(moment(updated), 'minutes') < 15
    },

    startPolling() {
      if (this.pollingTimer) return
      this.pollingActive = true
      this.pollingTimer = setInterval(() => {
        const hasActiveJobs = this.studentData.some(
          s => this.isRecentlyQueued(s)
        )
        if (hasActiveJobs) {
          this.fetchRoster(true)
        } else {
          this.stopPolling()
        }
      }, 5000)
    },

    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
      this.pollingActive = false
    },

    handleSingleGenerate(record) {
      this.$set(record, 'isGenerating', true)

      this.$http.post('/api/exam/marksheet/individual/generate-single', {
        enrollment_id: record.id,
      }).then(({ data }) => {
        this.$notification.info({
          message: 'Generation Queued',
          description: data.message || `Marksheet queued for ${record.student.first_name}.`,
          duration: 3,
        })

        // Instantly clear outdated state and mark as Initiated locally for immediate visual feedback
        if (record.is_outdated) {
          this.$set(record, 'is_outdated', false)
        }
        if (record.individual_marksheet) {
          record.individual_marksheet.status = 'Initiated'
        } else {
          this.$set(record, 'individual_marksheet', { status: 'Initiated' })
        }
        this.startPolling()
      }).catch(err => {
        const errorMsg = (err.response && err.response.data && err.response.data.message) || 'Unable to queue marksheet generation.'
        this.$notification.error({
          message: 'Failed to queue generation',
          description: errorMsg,
        })
      }).finally(() => {
        this.$set(record, 'isGenerating', false)
      })
    },

    handleReEvaluateSections() {
      if (!this.selectedClass) {
        this.$notification.warning({
          message: 'Select Class',
          description: 'Please select a Class before re-evaluating sections.',
        })
        return
      }

      const className = this.selectedClassName || 'the selected class'

      this.$confirm({
        title: `Re-evaluate All Sections for ${className}?`,
        content: `This will recalculate total marks, percentages, section ranks, and class ranks across all sections for ${className}. Any rank changes will make existing report card PDFs show as 'Outdated'.`,
        okText: 'Re-evaluate All',
        cancelText: 'Cancel',
        onOk: () => {
          this.evaluatingSections = true
          this.$http.post('/api/exam/marksheet/re-evaluate-class-sections', {
            session_id: this.selectedSession,
            class_id: this.selectedClass,
          }).then(({ data }) => {
            this.$notification.success({
              message: 'Re-evaluation Complete',
              description: data.message || `Successfully re-evaluated all sections for ${className}.`,
              duration: 4,
            })
            this.fetchRoster(false)
          }).catch(err => {
            const errorMsg = (err.response && err.response.data && err.response.data.message) || 'Failed to re-evaluate sections.'
            this.$notification.error({
              message: 'Re-evaluation Failed',
              description: errorMsg,
            })
          }).finally(() => {
            this.evaluatingSections = false
          })
        },
      })
    },

    handleBulkGenerate(selectedOnly = false) {
      if (!this.selectedClass) {
        this.$notification.warning({
          message: 'Select Class',
          description: 'Please select a Class before triggering bulk generation.',
        })
        return
      }

      const curClass = find(this.schoolClasses, ['id', this.selectedClass])
      const className = curClass ? curClass.name : ''
      let targetDesc = ''

      if (selectedOnly) {
        targetDesc = `${this.selectedRowKeys.length} selected students`
      } else if (this.selectedSection) {
        const curSec = find(this.sections, ['id', this.selectedSection])
        const secName = curSec ? curSec.name : ''
        targetDesc = `all students in ${className} - ${secName}`
      } else {
        targetDesc = `all students in ${className} (All Sections)`
      }

      const enrollmentIds = selectedOnly ? this.selectedRowKeys : null

      this.$confirm({
        title: 'Generate Marksheets?',
        content: `Are you sure you want to queue marksheet generation for ${targetDesc}?`,
        okText: 'Generate Now',
        cancelText: 'Cancel',
        onOk: () => {
          this.bulkGenerating = true
          this.$http.post('/api/exam/marksheet/generate-individual', {
            session_id: this.selectedSession,
            class_id: this.selectedClass,
            section_id: this.selectedSection,
            enrollment_ids: enrollmentIds,
            force: true,
          }).then(({ data }) => {
            this.$notification.success({
              message: 'Bulk Generation Queued',
              description: `Queued generation for ${data.count || ''} students. Real-time status will update automatically.`,
            })
            this.selectedRowKeys = []
            this.fetchRoster()
            this.startPolling()
          }).catch(err => {
            const errorMsg = (err.response && err.response.data && err.response.data.message) || 'Unable to queue marksheet generation.'
            this.$notification.error({
              message: 'Failed to start bulk generation',
              description: errorMsg,
            })
          }).finally(() => {
            this.bulkGenerating = false
          })
        },
      })
    },

    handlePreview(record) {
      this.previewTitle = `Report Card: ${record.student.first_name} ${record.student.last_name || ''} (${record.student_code})`
      this.previewLoading = true
      this.previewUrl = null
      this.previewModalVisible = true

      this.$http.get('/api/exam/marksheet/individual/preview', {
        params: { enrollment_id: record.id },
      }).then(({ data }) => {
        this.previewUrl = data.url
        this.previewLoading = false
      }).catch(err => {
        this.previewLoading = false
        const errorMsg = (err.response && err.response.data && err.response.data.message) || 'Unable to load report card preview.'
        this.$notification.error({
          message: 'Preview Unavailable',
          description: errorMsg,
        })
      })
    },

    handleDownload(record) {
      this.$set(record, 'isDownloading', true)
      this.$http.get('/api/exam/marksheet/individual/preview', {
        params: { enrollment_id: record.id },
      }).then(({ data }) => {
        if (data.url) {
          window.open(data.url, '_blank')
        }
      }).catch(err => {
        const errorMsg = (err.response && err.response.data && err.response.data.message) || 'Unable to download marksheet.'
        this.$notification.error({
          message: 'Download Failed',
          description: errorMsg,
        })
      }).finally(() => {
        this.$set(record, 'isDownloading', false)
      })
    },

    handleDelete(record) {
      if (!record.individual_marksheet || !record.individual_marksheet.id) return

      this.$http.delete(`/api/export/file/${record.individual_marksheet.id}`)
        .then(() => {
          this.$notification.success({
            message: 'Deleted Successfully',
            description: 'Report card PDF has been removed.',
          })
          record.individual_marksheet = null
          this.fetchRoster(true)
        })
        .catch(err => {
          const errorMsg = (err.response && err.response.data && err.response.data.message) || 'Unable to delete marksheet.'
          this.$notification.error({
            message: 'Delete Failed',
            description: errorMsg,
          })
        })
    },
  },
}
</script>

<style scoped>
.reportcard-management {
  font-family: inherit;
}

.stat-card {
  transition: all 0.2s ease-in-out;
}
.stat-card:hover {
  transform: translateY(-2px);
}

.border-left-success {
  border-left: 4px solid #46be8a !important;
}
.border-left-warning {
  border-left: 4px solid #fa8c16 !important;
}
.border-left-primary {
  border-left: 4px solid #0190fe !important;
}
.border-left-secondary {
  border-left: 4px solid #adb5bd !important;
}
.border-left-danger {
  border-left: 4px solid #fb434a !important;
}

.avatar-placeholder {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #e9ecef;
  color: #495057;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.status-tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
